/*
  Base Form validation

    Supported validators:
    - required                  (field is required. field === 0 is allowed)
    - email                     (makes sure email is either empty or valid)
    - length                    (validates length of input)
    - minOneArray               (requires array to have length > 0)
    - onlyLettersAndNumbers     (string is only allowed to be letters and numbers)
    - number                    (makes sure it's a number)
    - percentage                (makes sure number is between 0 and 100)
*/
export const requiredMessage = 'Required'
export const emailMessage = 'Invalid email address'
export const onlyLettersAndNumbersMessage = 'Please only use letters (a-z) and numbers.'
export const numbersMessage = 'Please only use numbers.'
export const numbersAndDashMessage = 'Please only use numbers and dashes.'
export const percentageMessage = 'Needs to be between 0 and 100'
export const minOneArrayMessage = 'Minimum of 1 item required'

export const validators = {
  // (kitti); should return error message if value is INVALID
  required: (value: number): string => (value || value === 0 ? '' : requiredMessage),
  email: (value: string): string => (!value || /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value) ? '' : emailMessage),
  length: (value: string | number, options: Array<number>): string => {
    // options [minLength, maxLength] | [null, maxLength] for only maxLength | [1] for only minLength
    const valueLength = String(value).length
    if (options.length === 1 && valueLength < options[0]) {
      return `Value needs to be at least ${options[0]} characters`
    }
    if (options[0] === null && valueLength > options[1]) {
      return `Value can't be longer than ${options[1]} characters`
    }
    if (valueLength < options[0] || valueLength > options[1]) {
      return `Value needs to be between ${options[0]} and ${options[1]} characters`
    }
    return ''
  },
  maxDecimals: (value: string | number, options: number): string => {
    if (!value && value !== 0) {
      return ''
    }
    const decimals = +value % 1 !== 0 ? value.toString().split('.')[1].length : 0
    if (decimals > options) {
      return `Ensure that there are no more than ${options} decimals`
    }
    return ''
  },
  percentage: (value: string): string => (+value < 0 || +value > 100 ? percentageMessage : ''),
  minOneArray: (value: Array<unknown>): string => (value.length > 0 ? '' : minOneArrayMessage),
  onlyLettersAndNumbers: (value: string): string => (!value || /^[a-zA-Z0-9]+$/i.test(value) ? '' : onlyLettersAndNumbersMessage),
  number: (value: string): string => (!value || /^[0-9]+$/i.test(value) ? '' : numbersMessage),
  numberOrDash: (value: string): string => (!value || /^[0-9]+$|^-$/i.test(value) ? '' : numbersAndDashMessage),
}

type ValidatorInput<T, P> = {
  fields: Array<Partial<T | P>>
  message?: string
}

type ConfigType<T, P> = {
  required?: ValidatorInput<T, P>
  length?: ValidatorInput<T, P> & { options: [number | null, number | null] | [number] }
  email?: ValidatorInput<T, P>
  onlyLettersAndNumbers?: ValidatorInput<T, P>
  numberOrDash?: ValidatorInput<T, P>
  minOneArray?: ValidatorInput<T, P>
  percentage?: ValidatorInput<T, P>
  maxDecimals?: ValidatorInput<T, P> & { options: number }
}

export const baseFormValidation =
  <Values, ValuesSubProps extends string>(config: ConfigType<keyof Values, ValuesSubProps>) =>
  (values: Values): Partial<Record<keyof Values | ValuesSubProps, string | Array<string>>> => {
    // NOTE(Kitti): validate config object
    Object.keys(config)
      .map(configKey => ({ ...config[configKey], key: configKey }))
      .forEach(({ key, fields }) => {
        fields.forEach(field => {
          /* eslint-disable */
          if (typeof field !== 'string') {
            console.error(`Fields for "${key}" needs to an array of strings`)
            /* eslint-enable */
          }
        })
      })
    return Object.keys(config).reduce(
      (errors, validationKey) => ({
        ...errors,
        ...config[validationKey].fields.reduce((validationErrors, valueKey) => {
          const validator = validators[validationKey]
          const { message: overrideMessage, options } = config[validationKey]
          if (valueKey.includes('.')) {
            const [arrayKey, arrayValueKey, innerArrayValueKey] = valueKey.split('.')
            const arrayErrors = values[arrayKey].reduce((agg, val, index) => {
              const currentVal = val[arrayValueKey]
              const innerErrorMsg = Array.isArray(currentVal)
                ? currentVal.reduce((innerAgg, innerVal, innerIndex) => {
                    const errorMessage = validator(innerVal[innerArrayValueKey], options)
                    if (errorMessage) {
                      return {
                        ...innerAgg,
                        [`${arrayKey}[${index}].${arrayValueKey}[${innerIndex}].${innerArrayValueKey}`]: overrideMessage || errorMessage,
                      }
                    }
                    return innerAgg
                  }, {})
                : {}
              const errorMessage = validator(val[arrayValueKey], options)
              if (errorMessage) {
                return {
                  ...agg,
                  ...innerErrorMsg,
                  [`${arrayKey}[${index}].${arrayValueKey}`]: overrideMessage || errorMessage,
                }
              }
              return { ...agg, ...innerErrorMsg }
            }, {})
            return {
              ...validationErrors,
              ...arrayErrors,
            }
          }
          const errorMessage = validator(values[valueKey], options)
          if (errorMessage) {
            return {
              ...validationErrors,
              [valueKey]: overrideMessage || errorMessage,
            }
          }
          return validationErrors
        }, {}),
      }),
      {},
    )
  }
